import { type CSSProperties, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  OnboardingAnswers,
  QuestionDefinition,
  SectionDefinition
} from '../../types/onboarding';
import { shouldShowQuestion } from '../../onboarding/utils';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { ZipCodeField } from './ZipCodeField';
import { CTAGroup } from '../ui/OnboardingCTA';
import { TooltipIcon as TooltipIconBase, CheckmarkIcon, ChevronDownIcon, AlertIcon } from '../icons';

const COUNTRY_CODES = [
  { code: '+54', label: 'Argentina (+54)' },
  { code: '+61', label: 'Australia (+61)' },
  { code: '+591', label: 'Bolivia (+591)' },
  { code: '+55', label: 'Brazil (+55)' },
  { code: '+56', label: 'Chile (+56)' },
  { code: '+57', label: 'Colombia (+57)' },
  { code: '+506', label: 'Costa Rica (+506)' },
  { code: '+53', label: 'Cuba (+53)' },
  { code: '+593', label: 'Ecuador (+593)' },
  { code: '+503', label: 'El Salvador (+503)' },
  { code: '+33', label: 'France (+33)' },
  { code: '+49', label: 'Germany (+49)' },
  { code: '+502', label: 'Guatemala (+502)' },
  { code: '+592', label: 'Guyana (+592)' },
  { code: '+509', label: 'Haiti (+509)' },
  { code: '+504', label: 'Honduras (+504)' },
  { code: '+91', label: 'India (+91)' },
  { code: '+353', label: 'Ireland (+353)' },
  { code: '+39', label: 'Italy (+39)' },
  { code: '+81', label: 'Japan (+81)' },
  { code: '+52', label: 'Mexico (+52)' },
  { code: '+31', label: 'Netherlands (+31)' },
  { code: '+64', label: 'New Zealand (+64)' },
  { code: '+505', label: 'Nicaragua (+505)' },
  { code: '+1', label: 'North America (+1)' },
  { code: '+507', label: 'Panama (+507)' },
  { code: '+595', label: 'Paraguay (+595)' },
  { code: '+51', label: 'Peru (+51)' },
  { code: '+7', label: 'Russia (+7)' },
  { code: '+65', label: 'Singapore (+65)' },
  { code: '+27', label: 'South Africa (+27)' },
  { code: '+82', label: 'South Korea (+82)' },
  { code: '+34', label: 'Spain (+34)' },
  { code: '+597', label: 'Suriname (+597)' },
  { code: '+46', label: 'Sweden (+46)' },
  { code: '+41', label: 'Switzerland (+41)' },
  { code: '+971', label: 'United Arab Emirates (+971)' },
  { code: '+44', label: 'United Kingdom (+44)' },
  { code: '+598', label: 'Uruguay (+598)' },
  { code: '+58', label: 'Venezuela (+58)' },
];

type OnboardingQuestionStepProps = {
  section: SectionDefinition;
  question: QuestionDefinition;
  stepIndex: number;
  totalSteps: number;
  progressLabel: string;
  answers: OnboardingAnswers;
  onAnswerChange: (id: string, value: string | string[] | boolean | undefined) => void;
  onBack: () => void;
  onContinue: () => void;
  isLoading?: boolean;
};

type ErrorMap = Record<string, string | undefined>;

type QuestionFieldProps = {
  question: QuestionDefinition;
  value: string | string[] | boolean | undefined;
  onChange: (value: string | string[] | boolean | undefined) => void;
  hasError?: boolean;
};

const OPTION_OTHER_FIELD_CONFIG: Record<string, { answerId: string; label: string; placeholder?: string }> = {
  experience_types: {
    answerId: 'experience_types_other',
    label: 'If Other, please specify'
  },
  option_types: {
    answerId: 'option_types_other',
    label: 'If Other, please specify'
  }
};

const GROUP_OPTION_OTHER_FIELD_CONFIG: Record<string, { answerId: string; label: string; placeholder?: string }> = {
  focus_unsure_pref: {
    answerId: 'focus_other_text',
    label: 'If Other, please specify'
  }
};

export const OTHER_FIELD_ERROR_MESSAGE = 'Please provide details for your selection.';

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

// Helper functions to handle options that can be either strings or objects
const getOptionValue = (option: string | { value: string; label: string; sublabel?: string }): string => {
  return typeof option === 'string' ? option : option.value;
};

const getOptionLabel = (option: string | { value: string; label: string; sublabel?: string }): string => {
  return typeof option === 'string' ? option : option.label;
};

const getOptionSublabel = (option: string | { value: string; label: string; sublabel?: string }): string | undefined => {
  return typeof option === 'string' ? undefined : option.sublabel;
};


function InfoNoteIcon() {
  return (
    <svg
      className="h-6 w-6 text-onboarding-primary"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z" />
    </svg>
  );
}

function InfoNote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-[16px] border border-onboarding-primary/30 bg-white/50 p-3">
      <div className="shrink-0">
        <InfoNoteIcon />
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-[20px] font-normal leading-[1.6] text-onboarding-primary">Note</p>
        <p className="text-[16px] leading-[1.6] text-black">{text}</p>
      </div>
    </div>
  );
}

function TooltipIcon() {
  return <TooltipIconBase className="text-[#CA9D35]" />;
}

function TooltipSmall({ boldText, regularText }: { boldText: string; regularText: string }) {
  return (
    <div className="flex items-start gap-[10px] rounded-2xl border border-onboarding-card-green px-4 py-3">
      <div className="shrink-0">
        <TooltipIcon />
      </div>
      <div className="flex flex-col gap-1 text-onboarding-card-green">
        <p className="font-helvetica text-base font-bold leading-[1.6]">{boldText}</p>
        <p className="font-helvetica text-sm font-normal leading-[1.4]">{regularText}</p>
      </div>
    </div>
  );
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <ChevronDownIcon
      className={cx(
        'text-[#214d3c] transition-transform duration-300 ease-out',
        isOpen && 'rotate-180'
      )}
    />
  );
}

type AccordionItemProps = {
  groupId: string;
  label: string;
  selectedCount: number;
  totalCount: number;
  isOpen: boolean;
  onToggle: () => void;
  hasError?: boolean;
  children: React.ReactNode;
};

function AccordionItem({ groupId, label, selectedCount, totalCount, isOpen, onToggle, hasError, children }: AccordionItemProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(isOpen ? undefined : 0);
  const hasSelections = selectedCount > 0;

  useEffect(() => {
    if (!contentRef.current) return;

    if (isOpen) {
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);
      // After animation completes, set to auto to handle dynamic content
      const timeout = setTimeout(() => setHeight(undefined), 300);
      return () => clearTimeout(timeout);
    } else {
      // First set to current height to enable transition
      const contentHeight = contentRef.current.scrollHeight;
      setHeight(contentHeight);
      // Then set to 0 on next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setHeight(0);
        });
      });
    }
  }, [isOpen]);

  return (
    <div
      className={cx(
        'overflow-hidden rounded-2xl border transition-all duration-200',
        hasError ? 'border-red-400' : 'border-[#214d3c]',
        hasSelections && 'bg-[#214d3c]/5'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${groupId}`}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition-colors"
      >
        <div className="flex flex-col gap-1">
          <span className="text-base font-bold leading-[1.6] text-[#214d3c]">{label}</span>
          {!isOpen && (
            <span className="text-sm leading-[1.6] text-[#214d3c]">
              {hasSelections ? `${selectedCount} selected` : `${totalCount} options available`}
            </span>
          )}
        </div>
        <div className="flex items-center shrink-0">
          <ChevronIcon isOpen={isOpen} />
        </div>
      </button>
      <div
        id={`accordion-content-${groupId}`}
        ref={contentRef}
        style={{ height: height !== undefined ? `${height}px` : 'auto' }}
        className="overflow-hidden transition-[height] duration-300 ease-out"
      >
        <div className="px-4 pb-4">
          {children}
        </div>
      </div>
    </div>
  );
}

const INPUT_BASE_CLASSES =
  'w-full rounded-xl border border-onboarding-primary/30 bg-onboarding-content-bg px-5 py-[18px] text-base text-onboarding-text-dark transition focus:border-onboarding-primary focus:outline-none';

const TEXTAREA_CLASSES = `${INPUT_BASE_CLASSES} min-h-[128px] resize-vertical`;

const CHOICE_CARD_CLASSES =
  'flex flex-col gap-3';

const CHIP_BASE_CLASSES =
  'mb-6 flex items-start gap-4 rounded-[26px] border border-onboarding-card-green bg-onboarding-card-green px-6 py-5 text-onboarding-text-light shadow-[0_28px_48px_-28px_rgba(15,8,27,0.6)]';

const CHIP_ICON_WRAPPER_CLASSES =
  'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF6D2]/12';

const CHIP_ICON_STYLE: CSSProperties = {
  backgroundColor: '#FFF6D2',
  maskImage: 'url(/icon.svg)',
  WebkitMaskImage: 'url(/icon.svg)',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
  maskSize: 'contain',
  WebkitMaskSize: 'contain'
};

export const isOtherSelected = (value: string | string[] | boolean | undefined) => {
  if (Array.isArray(value)) {
    return value.some(option => typeof option === 'string' && option.trim().toLowerCase() === 'other');
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'other';
  }
  return false;
};

const emailRegex = /.+@.+\..+/;

// Helper to validate phone number (10 digits for NA, 7-15 for others)
export const isValidPhoneNumber = (phone: string, countryCode: string): boolean => {
  const digits = phone.replace(/\D/g, '');
  // North America requires exactly 10 digits
  if (countryCode === '+1') {
    return digits.length === 10;
  }
  // Other countries: 7-15 digits
  return digits.length >= 7 && digits.length <= 15;
};

// Helper to extract country code from stored phone value
export const parseStoredPhone = (storedValue: string | undefined): { countryCode: string; phoneNumber: string } => {
  if (!storedValue || typeof storedValue !== 'string') {
    return { countryCode: '+1', phoneNumber: '' };
  }
  // Try to match against known country codes (sorted by length descending to match longest first)
  const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const country of sortedCodes) {
    if (storedValue.startsWith(country.code)) {
      return { countryCode: country.code, phoneNumber: storedValue.slice(country.code.length) };
    }
  }
  // If no match, return as-is with default country code
  return { countryCode: '+1', phoneNumber: storedValue };
};

// Format phone number for display (North America: (555) 123-4567)
export const formatPhoneForDisplay = (digits: string, countryCode: string): string => {
  // Only format for North America
  if (countryCode === '+1') {
    const cleaned = digits.replace(/\D/g, '');
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
  // For other countries, just return as-is
  return digits;
};

// Extract raw digits from formatted phone
const extractDigits = (formatted: string): string => {
  return formatted.replace(/\D/g, '');
};


export function validateQuestion(
  question: QuestionDefinition,
  value: string | string[] | boolean | undefined,
  answers: OnboardingAnswers
): string | undefined {
  const isRequired = Boolean(question.required);

  switch (question.type) {
    case 'text':
    case 'country':
      if (!isRequired) {
        return undefined;
      }
      if (!value || typeof value !== 'string' || value.trim() === '') {
        return 'This field is required.';
      }
      // Light validation for zip/postal code — non-blocking for international formats
      if (question.id === 'location_postcode') {
        const trimmed = value.trim();
        if (trimmed.length < 2 || trimmed.length > 12) {
          return 'Please enter a valid zip or postal code.';
        }
      }
      return undefined;
    case 'tel':
      if (!isRequired) {
        return undefined;
      }
      if (!value || typeof value !== 'string' || value.trim() === '') {
        return 'Phone number is required.';
      }
      // For phone fields, validate the phone number part (after country code)
      const { phoneNumber } = parseStoredPhone(value);
      if (!phoneNumber || phoneNumber.trim() === '') {
        return 'Phone number is required.';
      }
      const { countryCode } = parseStoredPhone(value);
      if (!isValidPhoneNumber(phoneNumber, countryCode)) {
        if (countryCode === '+1') {
          return 'Enter a valid 10-digit phone number.';
        }
        return 'Enter a valid phone number (7-15 digits).';
      }
      return undefined;
    case 'email':
      if (!value || typeof value !== 'string' || value.trim() === '') {
        return isRequired ? 'Email is required.' : undefined;
      }
      if (!emailRegex.test(value.trim())) {
        return 'Enter a valid email address.';
      }
      return undefined;
    case 'textarea':
      if (!isRequired) {
        return undefined;
      }
      if (!value || typeof value !== 'string' || value.trim() === '') {
        return 'This field is required.';
      }
      return undefined;
    case 'singleselect':
      if (!value || typeof value !== 'string') {
        return isRequired ? 'Select an option to continue.' : undefined;
      }
      if (isOtherSelected(value)) {
        const otherConfig = OPTION_OTHER_FIELD_CONFIG[question.id];
        if (otherConfig) {
          const otherValue = answers[otherConfig.answerId];
          if (!otherValue || typeof otherValue !== 'string' || otherValue.trim() === '') {
            return OTHER_FIELD_ERROR_MESSAGE;
          }
        }
      }
      return undefined;
    case 'multiselect':
      if (!Array.isArray(value) || value.length === 0) {
        return isRequired ? 'Select at least one option.' : undefined;
      }
      {
        const otherConfig = OPTION_OTHER_FIELD_CONFIG[question.id];
        if (otherConfig && isOtherSelected(value)) {
          const otherValue = answers[otherConfig.answerId];
          if (!otherValue || typeof otherValue !== 'string' || otherValue.trim() === '') {
            return OTHER_FIELD_ERROR_MESSAGE;
          }
        }
      }
      return undefined;
    case 'checkbox':
      if (!value) {
        return isRequired ? 'Please check to continue.' : undefined;
      }
      return undefined;
    case 'grouped-multiselect':
      return undefined;
    default:
      return undefined;
  }
}

function QuestionField({ question, value, onChange, hasError }: QuestionFieldProps) {
  // For phone fields, we need to manage country code and display value separately
  // We store the combined value in parent, but display them separately
  const [localCountryCode, setLocalCountryCode] = useState(() => {
    if (question.type === 'tel') {
      const parsed = parseStoredPhone(typeof value === 'string' ? value : undefined);
      return parsed.countryCode;
    }
    return '+1';
  });
  const [localPhoneNumber, setLocalPhoneNumber] = useState(() => {
    if (question.type === 'tel') {
      const parsed = parseStoredPhone(typeof value === 'string' ? value : undefined);
      // Extract just digits in case there's any formatting in stored value
      return extractDigits(parsed.phoneNumber);
    }
    return '';
  });

  // Sync local phone state to parent whenever local values change
  // This ensures validation works even without blur, while keeping display separate
  useEffect(() => {
    if (question.type === 'tel') {
      const combined = localPhoneNumber.trim() ? `${localCountryCode}${localPhoneNumber.trim()}` : '';
      // Only sync if the value is actually different to avoid infinite loops
      const currentValue = typeof value === 'string' ? value : '';
      if (combined !== currentValue) {
        onChange(combined);
      }
    }
  }, [localCountryCode, localPhoneNumber, question.type]);

  switch (question.type) {
    case 'text':
    case 'email':
    case 'country':
      if (question.id === 'location_postcode') {
        return (
          <ZipCodeField
            question={question}
            value={typeof value === 'string' ? value : ''}
            onChange={onChange}
            hasError={hasError}
          />
        );
      }
      return (
        <div className="relative">
          <input
            id={question.id}
            className={cx(
              INPUT_BASE_CLASSES,
              "font-['Helvetica Neue','Arial',sans-serif]",
              'placeholder:text-[#707C8B]',
              hasError && 'border-red-600 pr-12'
            )}
            type={question.type === 'country' ? 'text' : question.type}
            inputMode={question.type === 'email' ? 'email' : 'text'}
            value={typeof value === 'string' ? value : ''}
            onChange={event => onChange(event.target.value)}
            placeholder={question.label}
          />
          {hasError && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
              <AlertIcon />
            </div>
          )}
        </div>
      );
    case 'tel':
      return (
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <select
              id={`${question.id}-country-code`}
              value={localCountryCode}
              onChange={(e) => {
                setLocalCountryCode(e.target.value);
                // Re-format the phone number when country changes
                const digits = extractDigits(localPhoneNumber);
                setLocalPhoneNumber(formatPhoneForDisplay(digits, e.target.value));
              }}
              className={cx(
                'w-[110px] sm:w-[220px] rounded-xl border border-onboarding-primary/30 bg-onboarding-bg px-3 sm:px-4 py-[18px] text-sm sm:text-base text-onboarding-text-dark transition focus:border-onboarding-primary focus:outline-none',
                hasError && 'border-red-600'
              )}
            >
              {COUNTRY_CODES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.label}
                </option>
              ))}
            </select>
            <div className="relative flex-1">
              <input
                id={question.id}
                className={cx(
                  INPUT_BASE_CLASSES,
                  "font-['Helvetica Neue','Arial',sans-serif]",
                  'placeholder:text-[#707C8B]',
                  hasError && 'border-red-600 pr-12'
                )}
                type="tel"
                inputMode="tel"
                value={formatPhoneForDisplay(localPhoneNumber, localCountryCode)}
                onChange={(e) => {
                  // Extract digits and store raw, but display formatted
                  const digits = extractDigits(e.target.value);
                  // Limit to 10 digits for NA, 15 for others
                  const maxDigits = localCountryCode === '+1' ? 10 : 15;
                  const limitedDigits = digits.slice(0, maxDigits);
                  setLocalPhoneNumber(limitedDigits);
                }}
                placeholder={localCountryCode === '+1' ? '(555) 123-4567' : 'Phone number'}
              />
              {hasError && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                  <AlertIcon />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    case 'textarea':
      return (
        <div className="relative">
          <textarea
            id={question.id}
            className={cx(
              TEXTAREA_CLASSES,
              "font-['Helvetica Neue','Arial',sans-serif]",
              'placeholder:text-[#707C8B]',
              hasError && 'border-red-600 pr-12'
            )}
            value={typeof value === 'string' ? value : ''}
            onChange={event => onChange(event.target.value)}
            rows={4}
            placeholder={(question as any).placeholder}
          />
          {hasError && (
            <div className="pointer-events-none absolute top-0 right-0 flex items-start pt-[18px] pr-4">
              <AlertIcon />
            </div>
          )}
        </div>
      );
    case 'singleselect':
      return (
        <div className="flex flex-col gap-6">
          {question.options.map((option, index) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const optionSublabel = getOptionSublabel(option);
            const selected = value === optionValue;
            const optionId = `${question.id}-${index}`;
            return (
              <label
                key={optionValue}
                htmlFor={optionId}
                className="flex items-start gap-3 cursor-pointer"
              >
                <input
                  id={optionId}
                  type="radio"
                  name={question.id}
                  checked={selected}
                  onChange={() => onChange(optionValue)}
                  className="sr-only"
                />
                <span
                  aria-hidden
                  className={cx(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition',
                    selected
                      ? 'border-onboarding-card-green'
                      : 'border-onboarding-radio-border'
                  )}
                >
                  <span
                    className={cx(
                      'h-3 w-3 rounded-full transition',
                      selected ? 'bg-onboarding-card-green' : 'bg-transparent'
                    )}
                  />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="font-helvetica text-lg leading-6 text-black">{optionLabel}</span>
                  {optionSublabel && (
                    <span className="font-helvetica text-sm font-normal leading-snug text-onboarding-primary/60">{optionSublabel}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      );
    case 'multiselect':
      return (
        <div className="flex flex-col gap-6">
          {question.options.map((option, index) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const optionSublabel = getOptionSublabel(option);
            const selected = Array.isArray(value) ? value.includes(optionValue) : false;
            const optionId = `${question.id}-${index}`;
            return (
              <label
                key={optionValue}
                htmlFor={optionId}
                className="flex items-start gap-3 cursor-pointer"
              >
                <input
                  id={optionId}
                  type="checkbox"
                  checked={selected}
                  className="sr-only"
                  onChange={() => {
                    const current = Array.isArray(value) ? [...value] : [];

                    // Special handling for clinical_diagnoses "None" option
                    if (question.id === 'clinical_diagnoses') {
                      if (optionValue === 'None') {
                        // If selecting "None", clear all other options
                        if (!selected) {
                          onChange(['None']);
                        } else {
                          // If deselecting "None", just remove it
                          onChange([]);
                        }
                        return;
                      } else {
                        // If selecting any other option, remove "None" if it's selected
                        const withoutNone = current.filter(item => item !== 'None');
                        if (selected) {
                          onChange(withoutNone.filter(item => item !== optionValue));
                        } else {
                          onChange([...withoutNone, optionValue]);
                        }
                        return;
                      }
                    }

                    // Default multiselect behavior
                    if (selected) {
                      onChange(current.filter(item => item !== optionValue));
                    } else {
                      onChange([...current, optionValue]);
                    }
                  }}
                />
                <span
                  aria-hidden
                  className={cx(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border transition',
                    selected ? 'border-onboarding-card-green bg-onboarding-card-green' : 'border-[#a0978a]'
                  )}
                >
                  {selected && (
                    <CheckmarkIcon className="h-3.5 w-3.5 text-white" />
                  )}
                </span>
                <span className="flex flex-col">
                  <span className="font-helvetica text-lg font-medium leading-[1.4] tracking-[-0.09px] text-black">{optionLabel}</span>
                  {optionSublabel && (
                    <span className="font-helvetica text-sm font-normal leading-[1.6] text-[#707c8b]">{optionSublabel}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      );
    case 'checkbox':
      return (
        <label
          className="flex items-start gap-3 rounded-2xl border border-onboarding-primary/30 bg-onboarding-bg px-6 py-5 text-base text-onboarding-text-dark"
          htmlFor={question.id}
        >
          <input
            id={question.id}
            type="checkbox"
            checked={Boolean(value)}
            onChange={event => onChange(event.target.checked)}
            className="mt-1 h-[20px] w-[20px] shrink-0 cursor-pointer appearance-none rounded border border-onboarding-primary/40 transition hover:border-onboarding-primary focus:outline-none focus:ring-2 focus:ring-onboarding-primary/40 checked:border-onboarding-primary checked:bg-onboarding-primary"
          />
          <span>{question.label}</span>
        </label>
      );
    default:
      return null;
  }
}

export function OnboardingQuestionStep({
  section,
  question,
  stepIndex,
  totalSteps,
  progressLabel,
  answers,
  onAnswerChange,
  onBack,
  onContinue,
  isLoading = false
}: OnboardingQuestionStepProps) {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ErrorMap>({});
  const handleBackClick = () => {
    if (stepIndex === 0) {
      navigate('/');
      return;
    }
    onBack();
  };
  const [groupErrors, setGroupErrors] = useState<ErrorMap>({});
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());

  const toggleAccordion = (groupId: string) => {
    setOpenAccordions(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Reset scroll position and accordion state when question changes
  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset accordion state for new question - only open groups that have selections
    if (question.type === 'grouped-multiselect' && question.groups.length > 0) {
      const groupsWithSelections = question.groups
        .filter(group => {
          const value = answers[group.id];
          return Array.isArray(value) && value.length > 0;
        })
        .map(group => group.id);

      setOpenAccordions(new Set(groupsWithSelections));
    } else {
      setOpenAccordions(new Set());
    }
  }, [question.id]);

  const isVisible = useMemo(
    () => shouldShowQuestion(question, answers),
    [question, answers]
  );

  const progress = totalSteps > 0 ? ((stepIndex + 1) / totalSteps) * 100 : 0;

  if (!isVisible) {
    return null;
  }

  const handleContinue = () => {
    if (question.type === 'grouped-multiselect') {
      const nextGroupErrors: ErrorMap = {};
      question.groups.forEach(group => {
        const value = answers[group.id];
        if (group.required) {
          if (!Array.isArray(value) || value.length === 0) {
            nextGroupErrors[group.id] = 'Select at least one option.';
            return;
          }
        }

        const groupOtherConfig = GROUP_OPTION_OTHER_FIELD_CONFIG[group.id];
        if (groupOtherConfig && Array.isArray(value) && isOtherSelected(value)) {
          const otherValue = answers[groupOtherConfig.answerId];
          if (!otherValue || typeof otherValue !== 'string' || otherValue.trim() === '') {
            nextGroupErrors[group.id] = OTHER_FIELD_ERROR_MESSAGE;
          }
        }
      });

      setGroupErrors(nextGroupErrors);
      if (Object.keys(nextGroupErrors).length === 0) {
        onContinue();
      }
      return;
    }

    const error = validateQuestion(question, answers[question.id], answers);
    setErrors({ [question.id]: error });
    if (!error) {
      onContinue();
    }
  };

  const titleText = question.label;
  const questionError = errors[question.id];
  const value = answers[question.id];
  const optionOtherConfig = OPTION_OTHER_FIELD_CONFIG[question.id];
  const otherFieldValue = optionOtherConfig && typeof answers[optionOtherConfig.answerId] === 'string'
    ? (answers[optionOtherConfig.answerId] as string)
    : '';
  const shouldShowOtherField = Boolean(
    optionOtherConfig && (
      isOtherSelected(value) ||
      otherFieldValue.trim().length > 0
    )
  );

  const clearQuestionError = () => {
    setErrors(prev => {
      if (!prev[question.id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[question.id];
      return next;
    });
  };

  const clearGroupError = (groupId: string) => {
    setGroupErrors(prev => {
      if (!prev[groupId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
  };

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-onboarding-content-bg">
        {/* Mobile header */}
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-onboarding-bg/25 bg-onboarding-header px-4">
          <button
            type="button"
            onClick={handleBackClick}
            aria-label="Go back"
            className="flex w-10 items-center justify-center text-onboarding-text-light transition-opacity hover:opacity-70"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Desktop header */}
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        {/* Content wrapper */}
        <div className="flex flex-1 flex-col lg:items-center lg:px-[72px] lg:py-[40px] lg:gap-[40px]">
          {/* Progress bar - full width on large screens */}
          <section className="flex shrink-0 flex-col gap-[6px] px-4 lg:px-0 pb-2 pt-4 lg:pt-0 lg:pb-0 w-full lg:max-w-[1296px]">
            <span className="font-helvetica text-[14px] leading-[1.6] text-onboarding-primary">{progressLabel}</span>
            <div className="h-2 w-full overflow-hidden rounded-full bg-[#C9C9C9]">
              <div
                className="h-full rounded-full bg-onboarding-accent transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </section>

          {/* Main content area */}
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col gap-6 lg:gap-[24px] overflow-y-auto lg:overflow-visible px-4 lg:px-0 pb-10 lg:pb-[40px] pt-6 lg:pt-0 scroll-smooth w-full lg:w-[509px]">
            {/* Chip - full width on large screens */}
            {question.chip ? (
              <div className={`${CHIP_BASE_CLASSES} lg:max-w-none`}>
                <span className={CHIP_ICON_WRAPPER_CLASSES} aria-hidden>
                  <span className="block h-6 w-6" style={CHIP_ICON_STYLE} />
                </span>
                <div className="flex flex-col gap-1 text-[#FFF6D2]">
                  <p className="text-lg font-semibold leading-[1.2]">{question.chip.title}</p>
                  <p className="text-sm leading-relaxed text-[#FFF6D2]/85">
                    {question.chip.message}
                  </p>
                </div>
              </div>
            ) : null}

            {/* Question content */}
            <div className="flex flex-col w-full gap-[24px]">
            <div className="flex flex-col gap-6">
              <h1 className="font-recoleta text-[32px] font-normal leading-[1.1] text-onboarding-card-green">
                {titleText}
              </h1>
              {question.helper ? (
                <p className="font-helvetica text-[18px] leading-[1.4] text-black">
                  {question.helper}
                </p>
              ) : null}
            </div>

            {question.type === 'grouped-multiselect' ? (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                  {question.groups.map(group => {
                    const selected = Array.isArray(answers[group.id])
                      ? (answers[group.id] as string[])
                      : [];
                    const error = groupErrors[group.id];
                    const groupOtherConfig = GROUP_OPTION_OTHER_FIELD_CONFIG[group.id];
                    const groupOtherValue = groupOtherConfig && typeof answers[groupOtherConfig.answerId] === 'string'
                      ? (answers[groupOtherConfig.answerId] as string)
                      : '';
                    const shouldShowGroupOther = Boolean(
                      groupOtherConfig && (
                        isOtherSelected(selected) || groupOtherValue.trim().length > 0
                      )
                    );
                    const isOpen = openAccordions.has(group.id);

                    return (
                      <AccordionItem
                        key={group.id}
                        groupId={group.id}
                        label={group.label}
                        selectedCount={selected.length}
                        totalCount={group.options.length}
                        isOpen={isOpen}
                        onToggle={() => toggleAccordion(group.id)}
                        hasError={Boolean(error)}
                      >
                        <div className={CHOICE_CARD_CLASSES}>
                          {group.options.map(option => {
                            const normalizedOption = option.trim();
                            const isSelected = selected.some(
                              item => item.trim().toLowerCase() === normalizedOption.toLowerCase()
                            );
                            return (
                              <div
                                key={option}
                                className={cx(
                                  'flex items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 text-base transition hover:bg-onboarding-primary/5 cursor-pointer',
                                  isSelected
                                    ? 'font-semibold text-onboarding-text-dark'
                                    : 'text-onboarding-text-dark'
                                )}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const next = isSelected
                                    ? selected.filter(
                                        item =>
                                          item.trim().toLowerCase() !== normalizedOption.toLowerCase()
                                      )
                                    : [...selected, normalizedOption];
                                  onAnswerChange(group.id, next);
                                  clearGroupError(group.id);
                                  if (groupOtherConfig && !isOtherSelected(next)) {
                                    onAnswerChange(groupOtherConfig.answerId, undefined);
                                  }
                                }}
                                role="checkbox"
                                aria-checked={isSelected}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === ' ' || e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const next = isSelected
                                      ? selected.filter(
                                          item =>
                                            item.trim().toLowerCase() !== normalizedOption.toLowerCase()
                                        )
                                      : [...selected, normalizedOption];
                                    onAnswerChange(group.id, next);
                                    clearGroupError(group.id);
                                    if (groupOtherConfig && !isOtherSelected(next)) {
                                      onAnswerChange(groupOtherConfig.answerId, undefined);
                                    }
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  className="sr-only pointer-events-none"
                                  tabIndex={-1}
                                  readOnly
                                  aria-hidden="true"
                                />
                                <span
                                  aria-hidden
                                  className={cx(
                                    'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border border-onboarding-primary/40 transition',
                                    isSelected && 'border-onboarding-primary bg-onboarding-primary'
                                  )}
                                >
                                  {isSelected && (
                                    <CheckmarkIcon className="h-3 w-3 text-white" />
                                  )}
                                </span>
                                <span className="text-sm text-onboarding-text-dark">{option}</span>
                              </div>
                            );
                          })}
                        </div>
                        {groupOtherConfig && shouldShowGroupOther ? (
                          <div className="mt-3 flex flex-col gap-2">
                            <label
                              htmlFor={`${group.id}-other`}
                              className="text-sm font-medium text-onboarding-text-dark"
                            >
                              {groupOtherConfig.label}
                            </label>
                            <div className="relative">
                              <input
                                id={`${group.id}-other`}
                                className={cx(
                                  INPUT_BASE_CLASSES,
                                  "font-['Helvetica Neue','Arial',sans-serif]",
                                  error && 'border-red-600 pr-12'
                                )}
                                type="text"
                                value={groupOtherValue}
                                onChange={event => {
                                  onAnswerChange(groupOtherConfig.answerId, event.target.value);
                                  clearGroupError(group.id);
                                }}
                                placeholder={groupOtherConfig.placeholder ?? 'Please specify'}
                              />
                              {error && (
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                                  <AlertIcon />
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null}
                        {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
                      </AccordionItem>
                    );
                  })}
                </div>

                {/* Standalone "Other" radio button for seeker_beliefs */}
                {question.standaloneOther && (() => {
                  const isStandaloneOtherSelected = answers[`${question.id}_standalone_other`] === true;

                  return (
                    <label
                      htmlFor={`${question.id}-standalone-other`}
                      className="flex items-start gap-3 cursor-pointer"
                    >
                      <input
                        id={`${question.id}-standalone-other`}
                        type="checkbox"
                        checked={isStandaloneOtherSelected}
                        onChange={() => {
                          onAnswerChange(`${question.id}_standalone_other`, !isStandaloneOtherSelected);
                        }}
                        className="sr-only"
                      />
                      <span
                        aria-hidden
                        className={cx(
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition',
                          isStandaloneOtherSelected
                            ? 'border-onboarding-card-green'
                            : 'border-[#a0978a]'
                        )}
                      >
                        <span
                          className={cx(
                            'h-3 w-3 rounded-full transition',
                            isStandaloneOtherSelected ? 'bg-onboarding-card-green' : 'bg-transparent'
                          )}
                        />
                      </span>
                      <span className="text-lg leading-6 text-black">Other</span>
                    </label>
                  );
                })()}
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                  <QuestionField
                    question={question}
                    value={value}
                    hasError={Boolean(questionError)}
                    onChange={nextValue => {
                      onAnswerChange(question.id, nextValue);
                      clearQuestionError();
                      if (optionOtherConfig && !isOtherSelected(nextValue)) {
                        onAnswerChange(optionOtherConfig.answerId, undefined);
                      }
                    }}
                  />

                  {optionOtherConfig && shouldShowOtherField ? (
                    <div className="mt-2 flex flex-col gap-2">
                      <label
                        htmlFor={`${question.id}-other`}
                        className="text-sm font-medium text-onboarding-text-dark"
                      >
                        {optionOtherConfig.label}
                      </label>
                      <div className="relative">
                        <input
                          id={`${question.id}-other`}
                          className={cx(
                            INPUT_BASE_CLASSES,
                            "font-['Helvetica Neue','Arial',sans-serif]",
                            questionError && 'border-red-600 pr-12'
                          )}
                          type="text"
                          value={otherFieldValue}
                          onChange={event => {
                            onAnswerChange(optionOtherConfig.answerId, event.target.value);
                            clearQuestionError();
                          }}
                          placeholder={optionOtherConfig.placeholder ?? 'Please specify'}
                        />
                        {questionError && (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                            <AlertIcon />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {questionError ? <p className="text-sm text-red-600">{questionError}</p> : null}
                </div>
              </div>
            )}
            </div>

            {/* Info note */}
            {question.note ? (
              <div className="w-full">
                <InfoNote text={question.note} />
              </div>
            ) : null}

            {/* Tooltip Small */}
            {question.tooltip ? (
              <div className="w-full">
                <TooltipSmall
                  boldText={question.tooltip.boldText}
                  regularText={question.tooltip.regularText}
                />
              </div>
            ) : null}

            {/* Buttons */}
            <div className="py-4 w-full">
              <CTAGroup
                onBack={handleBackClick}
                onContinue={handleContinue}
                isLoading={isLoading}
                className="w-full"
              />
            </div>

            {/* Disclaimer */}
            <p className="font-helvetica text-[14px] leading-[1.6] text-black w-full">
              {question.disclaimer || "Guides Collective is an educational and referral platform and does not provide therapy or medical services."}
            </p>
          </main>
        </div>
      </div>

    </div>
  );
}
